/*
 * DataTable — Figma TableHeader-Core (119:44940) and SuggestionTableRow (119:44962).
 *
 * | Part          | Spec                                                                                   |
 * | Header row    | --surface-header, bottom 1px --border-strong, padding-x 24, top radius 12, 40px + border |
 * | Header cell   | padding 12px 8px, gap 4; SemiBold 600 14px, line-height normal (14px box), --text-weak, ellipsis; 16px ⋮ menu icon; hover --interaction-hover |
 * | Header rule   | 1px --border-strong after each cell, full height (2px before the actions column)          |
 * | Body row      | --surface-default, bottom 1px --border-default, padding-x 24, 40px + border               |
 * | Body cell     | content padding-x 8; 400 14/20 --text-default, one line, ellipsis                         |
 * | Body rule     | 1px --border-weak after each cell, full height (2px before the actions column)            |
 * | Actions cell  | padding 4px 8px 4px 16px, gap 8                                                         |
 * Column widths are per column (Figma: 120, 90, 380, one fill column). Each fixed column is its
 * width + the 1px rule, as in Figma.
 * Not in the design data: row hover (--interaction-hover, shows the action key hints), row click
 * (pointer cursor; opens the row's detail where the page passes onRowClick; the row darkens
 * while pressed, unless the press is on a button or link in it), loading and empty states.
 * Rows that open under the row pressed (a ledger's lines, `rowMotion`) unfold out of it and fold
 * back into it (styles/index.css, .motion-row).
 * Active row: by default the hovered or focused row (fill + key hints). A page whose keys act
 * on one row passes `activeRowKey` (and `rowProps` to learn about hover and focus): that row,
 * and only it, gets the fill, the key hints and the focus ring ("Focus M" colour, inset), so
 * hover and keyboard focus look the same; a focused row that isn't the active one keeps a
 * thin ring.
 *
 * Scrolling (not in Figma): the table is its own scroll box, at most `maxHeight` high (by default
 * the screen less room for the pager below it, at least 320px). A longer table scrolls its rows
 * under the column header, which stays at the top of the box; a focused row scrolls clear of it.
 * The pager stays below the box.
 * Narrow screens and many columns (not in Figma): the row padding is the page gutter less the
 * cell's 8px (24px from 1280, 16px below, 8px on a phone), so the text lines up with the page.
 * Where the columns don't fit, at any screen width, the table scrolls sideways inside its box,
 * never the page: the fixed columns keep their widths and the fill column keeps `fillMinWidth`
 * (at most 45% of the screen). Only then, the `sticky` columns stay at the left edge, side by
 * side in their order (a column between two of them scrolls under them; the header cell over a
 * pinned column is pinned both ways), and with `stickyActions` the row actions at the right edge
 * (from 640px; on a phone they scroll with the row, which still opens its detail). The pinned
 * columns take at most half the box beside the pinned actions: where they don't fit, the first
 * ones scroll too and the last one (the product) stays; a sticky column is at most 45% of the
 * screen wide. A soft shadow marks a pinned edge only while content is under it: below the
 * header once rows have scrolled up, right of the pinned columns once they hold, left of the
 * actions while there is more to the right. A pinned cell keeps its row's hover and active fill.
 * When everything fits, nothing of this applies and the table is as designed.
 * On a touch screen the column menu takes taps 40px around its 16px icon.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, HTMLAttributes, KeyboardEvent, ReactNode, RefObject } from 'react'
import { cx } from './cx'
import { Icon } from './Icon'
import { dotsThreeVertical16 } from './icons'
import { PHONE, useMediaQuery } from './useMediaQuery'

export type SortDirection = 'asc' | 'desc'

export interface Column<Row> {
    key: string
    header: string
    /** The header only for screen readers (a checkbox column). */
    hideHeader?: boolean
    /** Fixed width in px (without the rule), or 'fill' for the one flexible column. */
    width: number | 'fill'
    render: (row: Row) => ReactNode
    sortable?: boolean
    /**
     * Stays in view at the left edge while the table scrolls sideways (e.g. the product). Several
     * columns may be pinned: they line up in their order, the columns between them scrolling
     * under them. Where they don't fit (more than half the box), the last one stays.
     */
    sticky?: boolean
}

/** Pointer and focus handlers a page can attach to every body row (e.g. to track the active row). */
export type DataTableRowProps = Pick<HTMLAttributes<HTMLDivElement>, 'onPointerEnter' | 'onPointerMove' | 'onPointerLeave' | 'onFocus' | 'onBlur'>

export interface DataTableLabels {
    sortAscending: string
    sortDescending: string
    columnMenu: (header: string) => string
    empty: string
    loading: string
}

/**
 * The default `maxHeight`: the screen less room for the pager below the table, at least 320px.
 * Scrolled to its top, the table and its pager fit the screen.
 */
export const TABLE_MAX_HEIGHT = 'max(320px, 100dvh - 96px)'
/** The widest a sticky column (or the fill column's minimum) gets on a narrow screen. */
const NARROW_CAP = '45vw'
/** The share of the box (beside the pinned actions) the pinned columns may take. */
const PIN_SHARE = 0.5
/** Row padding: the page gutter less the cell's own 8px. */
const ROW_X = 'px-[calc(var(--layout-page-padding-x)-8px)]'
// Pinned cells: opaque (the active row's fill over white). The soft edge shadows, on the side where
// content scrolls under them, follow data attributes on the scroll box (set while scrolling):
// data-scrolled-y under the header, data-edge-<n> right of the n-th pinned column (the last one that
// holds), data-more left of the actions. Beside a column, the shadow is an 8px gradient strip on
// each cell over its bottom rule, so it runs down the rows unbroken (a box shadow would shrink at
// every row's ends).
const PIN_HEADER = 'sticky z-[2] bg-[var(--surface-header)]'
const PIN_BODY = 'sticky z-[1]'
const STRIP_RIGHT =
    "after:pointer-events-none after:absolute after:top-0 after:-bottom-px after:left-full after:hidden after:w-[8px] after:bg-[linear-gradient(to_right,color-mix(in_srgb,var(--overlay)_30%,transparent),transparent)] after:content-['']"
const PIN_EDGE = ['group-data-[edge-0]/table:after:block', 'group-data-[edge-1]/table:after:block', 'group-data-[edge-2]/table:after:block', 'group-data-[edge-3]/table:after:block']
const EDGE_ATTRIBUTES = ['data-edge-0', 'data-edge-1', 'data-edge-2', 'data-edge-3']
const HEADER_SHADOW = 'group-data-[scrolled-y]/table:shadow-[0_8px_8px_-8px_var(--overlay)]'
const STRIP_LEFT =
    "sm:overflow-visible sm:before:pointer-events-none sm:before:absolute sm:before:top-0 sm:before:-bottom-px sm:before:right-full sm:before:hidden sm:before:w-[8px] sm:before:bg-[linear-gradient(to_left,color-mix(in_srgb,var(--overlay)_30%,transparent),transparent)] sm:before:content-[''] sm:group-data-[more]/table:before:block"
const STICKY_RIGHT_HEADER = cx('sm:sticky sm:right-0 sm:z-[2] sm:bg-[var(--surface-header)]', STRIP_LEFT)
const STICKY_RIGHT_BODY = cx('sm:sticky sm:right-0 sm:z-[1]', STRIP_LEFT)
const FILL = 'bg-[var(--surface-default)]'
const FILL_ACTIVE = '[background:linear-gradient(var(--interaction-hover),var(--interaction-hover)),var(--surface-default)]'
const SM_FILL = 'sm:bg-[var(--surface-default)]'
const SM_FILL_ACTIVE = 'sm:[background:linear-gradient(var(--interaction-hover),var(--interaction-hover)),var(--surface-default)]'
/** A clickable row while pressed (feedback on pointer-down), unless the press is on its own button or link. */
const PRESSED = '[&:active:not(:has(:is(button,a,input,select,textarea):active))]:bg-[var(--interaction-active)]'

function ColumnMenu({ header, labels, onSort }: { header: string; labels: DataTableLabels; onSort: (direction: SortDirection) => void }) {
    const [open, setOpen] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    useEffect(() => {
        if (!open) return
        const close = (event: PointerEvent) => {
            if (!ref.current?.contains(event.target as Node)) setOpen(false)
        }
        document.addEventListener('pointerdown', close)
        return () => document.removeEventListener('pointerdown', close)
    }, [open])
    return (
        <div ref={ref} className="relative flex shrink-0">
            <button
                type="button"
                aria-label={labels.columnMenu(header)}
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                className={cx(
                    'relative flex size-[16px] cursor-pointer items-center justify-center border-0 bg-transparent p-0 focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none',
                    "pointer-coarse:before:absolute pointer-coarse:before:-inset-[12px] pointer-coarse:before:content-['']",
                )}
            >
                <Icon src={dotsThreeVertical16} size={16} />
            </button>
            {open && (
                <div role="menu" className="absolute top-[calc(100%+8px)] right-0 z-20 flex min-w-[180px] flex-col overflow-clip rounded-[8px] bg-[var(--surface-raised)] shadow-[var(--shadow-popout)]">
                    {(['asc', 'desc'] as const).map((direction) => (
                        <button
                            key={direction}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                                onSort(direction)
                                setOpen(false)
                            }}
                            className="ss03-ss04 flex w-full cursor-pointer rounded-[8px] border-0 bg-transparent px-[8px] py-[6px] text-left text-[14px] leading-[20px] font-normal text-[var(--text-default)] hover:bg-[var(--interaction-hover)] pointer-coarse:py-[10px]"
                        >
                            {direction === 'asc' ? labels.sortAscending : labels.sortDescending}
                        </button>
                    ))}
                </div>
            )}
        </div>
    )
}

function Rule({ strong, double }: { strong?: boolean; double?: boolean }) {
    return <span aria-hidden className={cx('shrink-0 self-stretch', double ? 'w-[2px]' : 'w-px', strong ? 'bg-[var(--border-strong)]' : 'bg-[var(--border-weak)]')} />
}

function widthClass(width: number | 'fill') {
    return width === 'fill' ? 'min-w-px flex-1' : 'shrink-0'
}

/** A fixed column's CSS width: a sticky one is capped on a narrow screen. */
function cellWidth(column: { width: number | 'fill'; sticky?: boolean }): string | undefined {
    if (column.width === 'fill') return undefined
    return column.sticky ? `min(${column.width}px, ${NARROW_CAP})` : `${column.width}px`
}

/** The least width at which every column has its room (below it, the table scrolls sideways). */
export function tableMinWidth(columns: Pick<Column<unknown>, 'width' | 'sticky'>[], { actionsWidth, fillMinWidth }: { actionsWidth?: number; fillMinWidth: number }): string {
    const parts: string[] = ['2 * (var(--layout-page-padding-x) - 8px)']
    let px = 0
    columns.forEach((column) => {
        px += 1 // the rule after the column
        if (column.width === 'fill') {
            if (fillMinWidth > 0) parts.push(`min(${fillMinWidth}px, ${NARROW_CAP})`)
        } else if (column.sticky) parts.push(cellWidth(column)!)
        else px += column.width
    })
    if (actionsWidth !== undefined) px += actionsWidth + 1 // the rule before the actions is 2px
    return `calc(${[...parts, `${px}px`].join(' + ')})`
}

/** A column (with its rule) as laid out in the header row. */
export interface ColumnBox {
    key: string
    /** Its left edge in the unscrolled row, in px. */
    x: number
    width: number
}

/** A pinned column: where it holds while the table scrolls sideways. */
export interface Pin {
    key: string
    /** Its distance from the box's left edge while it holds, in px (the pinned columns before it). */
    left: number
    /** Its left edge in the unscrolled row: it holds once the table has scrolled `x - left`. */
    x: number
    width: number
}

/**
 * Which sticky columns pin, and where: the last one always, the ones before it (backwards) while
 * all of them take at most `room` px. They line up from the left edge in their order.
 */
export function pinColumns(sticky: ColumnBox[], room: number): Pin[] {
    if (sticky.length === 0) return []
    let first = sticky.length - 1
    let total = sticky[first]!.width
    while (first > 0 && total + sticky[first - 1]!.width <= room) {
        first -= 1
        total += sticky[first]!.width
    }
    let left = 0
    return sticky.slice(first).map((column) => {
        const pin = { key: column.key, left, x: column.x, width: column.width }
        left += column.width
        return pin
    })
}

/** The last pinned column that holds (content scrolls under it) at this scroll position, or -1. */
export function edgeIndex(pins: Pin[], scrollLeft: number): number {
    let edge = -1
    pins.forEach((pin, index) => {
        if (scrollLeft + pin.left > pin.x + 0.5) edge = index
    })
    return edge
}

interface ScrollState {
    /** The columns don't fit: the table scrolls sideways. */
    overflowing: boolean
    /** The pinned columns, in order (only while it overflows). */
    pins: Pin[]
}

const samePins = (a: Pin[], b: Pin[]) =>
    a.length === b.length && a.every((pin, i) => pin.key === b[i]!.key && pin.left === b[i]!.left && pin.x === b[i]!.x && pin.width === b[i]!.width)

/**
 * Keeps the scroll box's state: whether it scrolls sideways and which columns pin (React state,
 * on resize), and its scroll position as data attributes for the edge shadows (every frame).
 */
function useScrollBox(
    scroller: RefObject<HTMLDivElement | null>,
    table: RefObject<HTMLDivElement | null>,
    header: RefObject<HTMLDivElement | null>,
    stickyKeys: string,
    /** The actions column pins at the right edge (its width is not room for the pinned columns). */
    actionsPin: boolean,
): ScrollState {
    const [state, setState] = useState<ScrollState>({ overflowing: false, pins: [] })
    useLayoutEffect(() => {
        const box = scroller.current
        if (!box) return
        const keys = stickyKeys ? stickyKeys.split('\n') : []
        let pins: Pin[] = []
        const onScroll = () => {
            const max = box.scrollWidth - box.clientWidth
            // Not React state: these change on every scroll frame.
            box.toggleAttribute('data-scrolled', box.scrollLeft > 0)
            box.toggleAttribute('data-more', box.scrollLeft < max - 1)
            box.toggleAttribute('data-scrolled-y', box.scrollTop > 0)
            const edge = edgeIndex(pins, box.scrollLeft)
            EDGE_ATTRIBUTES.forEach((attribute, index) => box.toggleAttribute(attribute, index === edge))
        }
        const measure = () => {
            const overflowing = box.scrollWidth - box.clientWidth > 1
            const row = header.current
            pins = []
            if (overflowing && row && keys.length > 0) {
                const boxes = new Map<string, ColumnBox>()
                let x = parseFloat(getComputedStyle(row).paddingLeft) || 0
                let actions = 0
                for (const cell of Array.from(row.children) as HTMLElement[]) {
                    const key = cell.dataset.col
                    if (key !== undefined) boxes.set(key, { key, x, width: cell.offsetWidth })
                    else actions = cell.offsetWidth
                    x += cell.offsetWidth
                }
                const sticky = keys.map((key) => boxes.get(key)).filter((column): column is ColumnBox => column !== undefined)
                pins = pinColumns(sticky, (box.clientWidth - (actionsPin ? actions : 0)) * PIN_SHARE)
            }
            const next: ScrollState = { overflowing, pins }
            setState((current) => (current.overflowing === next.overflowing && samePins(current.pins, next.pins) ? current : next))
            onScroll()
        }
        measure()
        box.addEventListener('scroll', onScroll, { passive: true })
        const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null
        observer?.observe(box)
        if (table.current) observer?.observe(table.current)
        return () => {
            box.removeEventListener('scroll', onScroll)
            observer?.disconnect()
        }
    }, [scroller, table, header, stickyKeys, actionsPin])
    return state
}

export function DataTable<Row>({
    columns,
    rows,
    rowKey,
    actions,
    actionsWidth = 160,
    onSort,
    onRowKeyDown,
    onRowClick,
    labels,
    loading,
    activeRowKey,
    rowProps,
    fillMinWidth = 0,
    stickyActions = false,
    maxHeight = TABLE_MAX_HEIGHT,
    rowMotion,
}: {
    columns: Column<Row>[]
    rows: Row[]
    rowKey: (row: Row) => string
    /** Row actions (e.g. approve / dismiss icon buttons). */
    actions?: (row: Row) => ReactNode
    actionsWidth?: number
    onSort?: (key: string, direction: SortDirection) => void
    /** Keyboard shortcuts on a focused row. */
    onRowKeyDown?: (row: Row, event: KeyboardEvent<HTMLDivElement>) => void
    /** A click on the row outside its buttons and links (e.g. opens the row's detail). */
    onRowClick?: (row: Row) => void
    labels: DataTableLabels
    loading?: boolean
    /** The row the page's keys act on (controlled). Omit to track hover and focus inside the table. */
    activeRowKey?: string | null
    /** Pointer and focus handlers for each body row (called after the table's own). */
    rowProps?: (row: Row) => DataTableRowProps
    /** The fill column's least width in px (at most 45% of the screen): narrower, the table scrolls sideways. Default 0. */
    fillMinWidth?: number
    /** Keeps the actions column in view at the right edge while the table scrolls sideways (from 640px). */
    stickyActions?: boolean
    /**
     * The most the table is high (a CSS length, e.g. `calc(100dvh - 200px)`): longer, its rows
     * scroll inside it under the sticky header. Default TABLE_MAX_HEIGHT; 'none' grows with the rows.
     */
    maxHeight?: string
    /** A row that opened under another one ('open': it unfolds) or is folding back into it ('closed', kept on screen while it goes). */
    rowMotion?: (row: Row) => 'open' | 'closed' | undefined
}) {
    const [hoverRow, setActiveRow] = useState<string | null>(null)
    const controlled = activeRowKey !== undefined
    const activeRow = controlled ? activeRowKey : hoverRow
    const scroller = useRef<HTMLDivElement>(null)
    const table = useRef<HTMLDivElement>(null)
    const header = useRef<HTMLDivElement>(null)
    const phone = useMediaQuery(PHONE)
    const stickyKeys = columns
        .filter((column) => column.sticky)
        .map((column) => column.key)
        .join('\n')
    const { overflowing, pins } = useScrollBox(scroller, table, header, stickyKeys, stickyActions && actions !== undefined && !phone)
    const pinActions = overflowing && stickyActions && actions !== undefined
    const pinned = new Map(pins.map((pin, index) => [pin.key, { left: pin.left, index }]))
    const last = pins.at(-1)
    // Keyboard focus scrolls a cell clear of the pinned columns (and a row clear of the header).
    const pinnedWidth = last ? last.left + last.width : 0
    const focusable = Boolean(onRowKeyDown || onRowClick)

    /** The column wrapper's classes: pinned ones hold at the left edge while the table scrolls. */
    const columnClass = (column: Column<Row>, body: boolean, active = false) => {
        const pin = pinned.get(column.key)
        return cx('flex items-stretch', widthClass(column.width), pin && (body ? cx(PIN_BODY, active ? FILL_ACTIVE : FILL) : PIN_HEADER), pin && STRIP_RIGHT, pin && PIN_EDGE[pin.index])
    }
    const columnStyle = (column: Column<Row>): CSSProperties | undefined => {
        const pin = pinned.get(column.key)
        return pin ? { left: pin.left } : undefined
    }
    const actionsClass = (body: boolean, active = false) => pinActions && (body ? cx(STICKY_RIGHT_BODY, active ? SM_FILL_ACTIVE : SM_FILL) : STICKY_RIGHT_HEADER)
    // The focus ring: on the row, and while columns are pinned also over their opaque cells.
    const ring = (active: boolean, over: boolean) =>
        !controlled
            ? over
                ? 'hidden group-focus-visible/row:block shadow-[var(--focus-inset)]'
                : 'focus-visible:shadow-[var(--focus-inset)]'
            : active
              ? 'shadow-[inset_0_0_0_2px_var(--focus-ring)]'
              : over
                ? 'hidden group-focus-visible/row:block shadow-[inset_0_0_0_1px_var(--focus-ring)]'
                : 'focus-visible:shadow-[inset_0_0_0_1px_var(--focus-ring)]'

    return (
        <div
            ref={scroller}
            // Rows that can't be focused (a report): the table itself takes focus, so the keyboard can scroll it.
            tabIndex={onRowClick ? undefined : 0}
            className="group/table isolate w-full overflow-x-auto overflow-y-auto overscroll-x-contain scroll-pt-[41px] focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none"
            style={{ maxHeight, scrollPaddingLeft: pinnedWidth || undefined }}
        >
            <div ref={table} role="table" aria-busy={loading || undefined} className="flex w-full flex-col" style={{ minWidth: tableMinWidth(columns, { actionsWidth: actions ? actionsWidth : undefined, fillMinWidth }) }}>
                <div
                    ref={header}
                    role="row"
                    className={cx('sticky top-0 z-[4] flex h-[41px] w-full items-stretch rounded-t-[12px] border-b border-solid border-[var(--border-strong)] bg-[var(--surface-header)]', HEADER_SHADOW, ROW_X)}
                >
                    {columns.map((column, index) => (
                        <div key={column.key} data-col={column.key} className={columnClass(column, false)} style={columnStyle(column)}>
                            <div
                                role="columnheader"
                                className="flex min-w-px flex-1 items-center gap-[4px] px-[8px] py-[12px] hover:bg-[var(--interaction-hover)]"
                                style={column.width === 'fill' ? undefined : { width: cellWidth(column) }}
                            >
                                <span className={cx('h-[14px] min-w-px flex-1 overflow-x-clip overflow-y-visible text-[14px] leading-[normal] font-semibold text-ellipsis whitespace-nowrap text-[var(--text-weak)]', column.hideHeader && 'sr-only')}>
                                    {column.header}
                                </span>
                                {column.sortable && onSort && <ColumnMenu header={column.header} labels={labels} onSort={(direction) => onSort(column.key, direction)} />}
                            </div>
                            <Rule strong double={actions !== undefined && index === columns.length - 1} />
                        </div>
                    ))}
                    {actions && <div role="columnheader" aria-label="" className={cx('shrink-0 self-stretch', actionsClass(false))} style={{ width: actionsWidth }} />}
                </div>

                {loading && rows.length === 0 ? (
                    <p role="status" className="m-0 px-[var(--layout-page-padding-x)] py-[24px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">
                        {labels.loading}
                    </p>
                ) : rows.length === 0 ? (
                    <p className="m-0 px-[var(--layout-page-padding-x)] py-[24px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{labels.empty}</p>
                ) : (
                    rows.map((row) => {
                        const key = rowKey(row)
                        const active = activeRow === key
                        const extra = rowProps?.(row)
                        const over = overflowing && (pins.length > 0 || pinActions)
                        const motion = rowMotion?.(row)
                        return (
                            <div
                                key={key}
                                role="row"
                                data-state={motion}
                                inert={motion === 'closed' || undefined}
                                tabIndex={focusable ? 0 : undefined}
                                data-row-active={active || undefined}
                                onMouseEnter={() => setActiveRow(key)}
                                onMouseLeave={() => setActiveRow((current) => (current === key ? null : current))}
                                onFocus={(event) => {
                                    setActiveRow(key)
                                    extra?.onFocus?.(event)
                                }}
                                onBlur={extra?.onBlur}
                                onPointerEnter={extra?.onPointerEnter}
                                onPointerMove={extra?.onPointerMove}
                                onPointerLeave={extra?.onPointerLeave}
                                onKeyDown={onRowKeyDown ? (event) => onRowKeyDown(row, event) : undefined}
                                onClick={
                                    onRowClick
                                        ? (event) => {
                                              const target = event.target as HTMLElement
                                              if (target.closest('button, a, input, select, textarea, [role="menu"]')) return
                                              onRowClick(row)
                                          }
                                        : undefined
                                }
                                className={cx(
                                    'group/row flex h-[41px] w-full items-stretch border-b border-solid border-[var(--border-default)] outline-none',
                                    ROW_X,
                                    over && 'relative',
                                    onRowClick && cx('cursor-pointer', PRESSED),
                                    active ? 'bg-[var(--interaction-hover)]' : 'bg-[var(--surface-default)]',
                                    motion && 'motion-row',
                                    ring(active, false),
                                )}
                            >
                                {columns.map((column, index) => (
                                    <div key={column.key} className={columnClass(column, true, active)} style={columnStyle(column)}>
                                        <div role="cell" className="flex min-w-px flex-1 items-center overflow-clip px-[8px]" style={column.width === 'fill' ? undefined : { width: cellWidth(column) }}>
                                            <div className="block h-[20px] min-w-px flex-1 overflow-hidden text-[14px] leading-[20px] font-normal text-ellipsis whitespace-nowrap text-[var(--text-default)]">
                                                {column.render(row)}
                                            </div>
                                        </div>
                                        <Rule double={actions !== undefined && index === columns.length - 1} />
                                    </div>
                                ))}
                                {actions && (
                                    <div role="cell" className={cx('flex shrink-0 items-center gap-[8px] overflow-clip py-[4px] pr-[8px] pl-[16px]', actionsClass(true, active))} style={{ width: actionsWidth }}>
                                        {actions(row)}
                                    </div>
                                )}
                                {/* The row's ring, over the pinned cells' own fill while the table scrolls. */}
                                {over && ((controlled && active) || focusable) && (
                                    <span aria-hidden className={cx('pointer-events-none absolute inset-0 z-[3]', ring(active, true))} />
                                )}
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )
}
